/**
 * Single registry of feature gate names, shared by every consumer in the
 * monorepo — backend services (`@orn/api`, `@orn/core`, `@orn/mops-api`)
 * and frontend apps (`@orn/web`, `@orn/mops-web`).
 *
 * **Why this exists:** every feature gate reference — server-side
 * `FeatureGateController.gatePasses(vc, name)` or client-side
 * `useFeatureGate` / `flags.has(...)` — must round-trip through this
 * registry instead of being passed as a bare string literal. Two reasons:
 *
 *  1. **Typo-proof.** Callers consume {@link FeatureGateName}, so
 *     `useFeatureGate('feat-assitant')` (typo) is a compile error rather
 *     than a silent always-false at runtime.
 *  2. **Discoverability.** Every gate the codebase cares about lives in
 *     this one file — operators reading the code can see the full set
 *     without grepping.
 *
 * This package is a leaf with no workspace or runtime dependencies, so
 * both Node and browser bundles can import it without dragging in
 * backend or frontend code ([ORN-850](https://linear.app/go-mantle/issue/ORN-850)
 * tracked the consolidation from per-package mirrors to this single
 * source of truth).
 *
 * **Naming conventions** for the *string values* are documented in
 * `packages/core/docs/feature-gate-naming.md`. TL;DR: lowercase +
 * hyphens, prefix with `feat-` / `kill-` / `exp-` / `ops-` / `debug-`,
 * names are public on the wire and immutable after creation.
 *
 * **Adding a gate:** append a new member here, then create the gate row
 * in mops-web's `NewFeatureGateScene` with the matching `name`. The DB
 * row is the runtime source of truth; this file is the *type*-time
 * source of truth for which names are legal in source code.
 *
 * **Removing a gate:** delete every consumer call site first, then the
 * registry entry, then the DB row. Order matters — an orphaned consumer
 * reading a deleted gate fails closed (returns `false`), which is
 * usually fine but worth doing in the right order so you never ship an
 * intermediate state where the gate is "gone" from the registry but
 * still referenced.
 */
export const FeatureGate = {
  /**
   * Gates the consumer "Bills & Subscriptions" sidebar tab. Internal
   * employees only at launch ([ORN-690](https://linear.app/go-mantle/issue/ORN-690)).
   * Eventual graduation: rule swap → percent rollout → `ALL_USERS` via
   * mops-web's New/Edit Feature Gate scenes.
   */
  Obligations: 'feat-obligations',
  /**
   * Gates the entire consumer billing surface — the `subscribe` mutation and
   * the `eligibleBillingAccounts` query: while off, no subscription can be
   * created, no checkout data is served, and no bank account can be debited.
   * This is the only thing standing between an authenticated caller and a real
   * ACH mandate, so the surface must never be registered without it. Off
   * everywhere at launch — the DB row is the runtime source of truth;
   * graduation is internal beta → percent rollout via mops-web.
   *
   * The row needs a `WEB_CLIENT` (or `ALL_CLIENTS`) audience, not the form's
   * `SERVER_ONLY` default: the web offers Subscribe only when it reads the gate
   * open, and {@link FeatureGate.MembershipRequired} stands down unless it is.
   */
  BillingCheckout: 'feat-billing-checkout',
  /**
   * Whether the membership wall applies to a household: open, and every
   * surface that consults `viewerHasMembershipAccess` requires the household
   * to hold an entitling subscription.
   *
   * Polarity is the inverse of the usual `feat-` gate: closed means *nobody*
   * is walled, so every such surface answers as though entitlement did not
   * exist. Opening it turns the wall on for the household, which is why it is
   * dialed by allow list rather than percentage.
   *
   * Two invariants live in `entitlementAccess.ts` rather than in operator
   * discipline, because either one violated locks a household out with no way
   * back in: the wall stands down unless {@link FeatureGate.BillingCheckout}
   * is also open for the household *as the web client reads it*, audience
   * included (a household that cannot buy a membership cannot be required to
   * hold one, and a `SERVER_ONLY` checkout row hides Subscribe while passing
   * every server check), and the gate resolves against the
   * household's caregiver rather than the acting member, so a loved one is
   * never walled while the person who pays is not.
   */
  MembershipRequired: 'feat-membership-required',
  /**
   * Gates the avatar-dropdown `Debug` submenu and the `/debug/*` routes
   * (`Sentry test`, `Environment`). Internal employees only —
   * `IS_ADMIN` rule at launch ([ORN-691](https://linear.app/go-mantle/issue/ORN-691)).
   * Per the `debug-` prefix convention, this gate is expected to stay
   * pinned to internal audiences rather than graduating to `ALL_USERS`.
   */
  DebugTools: 'debug-tools',
  /**
   * Gates the `JsonDebugRow` on the transaction record page. Replaces the old `User.isAdmin`
   * client-side check so gating is operator-controlled via mops-web rather
   * than hard-coded to the admin email list ([ORN-791](https://linear.app/go-mantle/issue/ORN-791)).
   * Per the `debug-` prefix convention, expected to stay pinned to internal
   * audiences rather than graduating to `ALL_USERS`.
   */
  DebugTransactionDetails: 'debug-transaction-details',
  /**
   * Gates the assistant chat slash-command surface. When off, a leading
   * `/issue …` is treated as ordinary chat text and flows to the LLM — the
   * orchestrator only diverts a command when this gate passes. Internal testers
   * / dogfooders only; the `/issue` flow collects internal feedback on
   * assistant responses, so it is not expected to graduate to `ALL_USERS`.
   */
  AssistantCommands: 'feat-assistant-commands',
  /**
   * Gates the three caregiver tools that enroll a loved one and take that back
   * — `loved_one_invite`, `loved_one_invite_cancel`, and
   * `loved_one_revoke_access`. All three or none, so no session is offered a
   * take-back for a grant it could not have made. When off they are absent from
   * the tool registry entirely, which is a stronger off state than a runtime
   * refusal: a tool that is not there cannot be described to the model, chosen
   * by it, or argued into by a user.
   *
   * Off is the default and the failure mode. Inviting opens a group text with
   * someone who has agreed to nothing yet, so the gate is read per caller and a
   * caller that forgets to resolve it gets the tools withheld rather than
   * granted. Internal users first; expected to graduate once the enrollment
   * funnel has been watched on real households.
   */
  LovedOneInvitations: 'feat-loved-one-invitations',
  /**
   * Kill switch for the per-recipient work-item outreach pass — the
   * headless turn that decides which of a member's waiting items reach them
   * by text or email. The outreach sweep reads it per recipient under their
   * own viewer, so a rule can silence one household while the rest keep
   * going; ON means their rows wait unclaimed for the switch to flip back
   * rather than being dropped.
   *
   * Default OFF (the pass runs). It exists because a send is the one effect
   * nothing undoes.
   */
  KillWorkItemOutreachPass: 'kill-work-item-outreach-pass',
  /**
   * Internal-debug toggle for the assistant chat surface. When on, the server
   * includes each tool's name / args / output on the live `tool_started` /
   * `tool_completed` frames and the web client logs them to the browser
   * console in real time instead of rendering them — the consumer UI shows
   * only a spinner while a turn is in flight ([ORN-1342](https://linear.app/go-mantle/issue/ORN-1342)).
   * Internal employees only per the `debug-` prefix convention; not expected
   * to graduate to `ALL_USERS`.
   */
  DebugAssistant: 'debug-assistant',
  /**
   * Gates GraphQL subscriptions over the `/ws/graphql` socket
   * ([ORN-1524](https://linear.app/go-mantle/issue/ORN-1524)): the server
   * rejects ungated connections at `connection_init`, and the web client
   * skips subscribing (the lazy ws link then never connects).
   *
   * It doubles as the transport's kill switch, but it is a *drain*, not a
   * switch, and two bounds stack. `gatePasses` reads a process-local cache
   * with a 5-minute TTL held per API task, so for that long after the flip
   * some tasks still accept new sockets. Each socket then keeps its streams
   * until it turns over, because the gate is read once per connection and
   * never re-read per operation. Budget for both when using this to stop
   * something, and reach for a socket close if you need it to stop now.
   *
   * HTTP polling carries correctness throughout, which is what makes that
   * lag tolerable rather than an outage.
   */
  GraphqlSubscriptions: 'feat-graphql-subscriptions',
  /**
   * Gates seeding the weekly-digest default schedule — the Sunday-morning email
   * recapping the week in a family's financial world. A caregiver who fails it
   * is never seeded; one already holding the row keeps it. Experiment /
   * prototype: internal employees only at launch (`is_admin` rule) until
   * per-user preferences, cadence, and unsubscribe controls exist. Eventual
   * graduation via mops-web's New/Edit Feature Gate scenes.
   */
  AssistantWeeklyDigest: 'exp-assistant-weekly-digest',
  /**
   * Gates the Alerts Center — the notification bell, its unread badge, the
   * center surface, and the assistant composer's alert strip. The
   * per-transaction alert indicators (table chip, record alert banner) are not gated
   * by this — they render from the work-item read regardless.
   *
   * Read on both sides — client surfaces via `useFeatureGate`, the
   * unread-count query server-side via `gatePasses`, so a client that skips
   * its check still reads an empty result. The `feature_gates` row must
   * therefore carry a `WEB_CLIENT` audience: under the `SERVER_ONLY` column
   * default, `gatePasses` still passes but `enabledFeatureGates` omits the
   * name and every client surface silently disappears.
   *
   * Internal employees only at launch (`is_admin` rule). Eventual
   * graduation: rule swap → percent rollout → `ALL_USERS` via mops-web's
   * New/Edit Feature Gate scenes.
   */
  AlertsCenter: 'feat-alerts-center',
  /**
   * Gates investment accounts: while it passes, every Plaid link token asks
   * the institution to consent to Investments alongside what it asks for
   * today, and the household's investment accounts join the supported set
   * the assistant reads. Nothing is billed until holdings are fetched for an
   * Item. The web Accounts page also reads it, to section investment accounts
   * instead of listing them as unsupported, and so does the account pane, to
   * render an investment account instead of its "not yet supported" line, and
   * the transactions table and transaction page, to show a bank transaction's
   * investment pair; under a `SERVER_ONLY` row those reads are always false,
   * so the web follows only once the row is widened to a client audience.
   * Off in every deployed environment until the operator creates the row.
   */
  InvestmentAccounts: 'feat-investment-accounts',
  /**
   * Whether a link token asks for Plaid's Auth product, which is what lets an
   * account be tokenized for a bank debit.
   *
   * An entitlement guard, not a rollout one: naming a product the Plaid account
   * may not request fails `/link/token/create` outright, so a closed gate is
   * what keeps an unentitled request from breaking every link rather than
   * merely withholding the debit. Open it only once Plaid has granted the
   * product for that environment.
   *
   * The row needs a `WEB_CLIENT` audience, not the form's `SERVER_ONLY`
   * default: the debit refusal offers to reconnect the bank through a fresh
   * link only where that link will name Auth, which is this gate, so the web
   * reads it beside `ReplaceBankConnection`. A server-only row would mint
   * tokens that ask for Auth while the action that uses them stays hidden.
   */
  PlaidAuthConsent: 'feat-plaid-auth-consent',
  /**
   * Whether a household may replace a bank connection it holds with a fresh
   * Plaid link to the same institution, keeping its accounts and history on
   * the new Item. A closed gate refuses the link token that asks for it, so
   * the whole path turns off in one place.
   *
   * The row needs a `WEB_CLIENT` audience: the debit refusal offers the
   * reconnect action only when it reads this gate and `PlaidAuthConsent` open
   * together. Open it only where `PlaidAuthConsent` already is: a replace
   * token names Auth only under that gate, and without it the fresh link
   * would retire the Item and return the same refusal.
   */
  ReplaceBankConnection: 'feat-replace-bank-connection',
  /**
   * Whether the NUX goal engine raises the cards that bring a member's due
   * goals to them, one at a time. While it is closed no new card is raised, and
   * every card already raised still clears as its goal does.
   *
   * A `SERVER_ONLY` row: no client reads it. Off in every deployed environment
   * until each card has a click route in the app and the outreach pass sends
   * one as a tip rather than as news.
   */
  NuxNudges: 'feat-nux-nudges',
} as const;

/**
 * Union of every value in {@link FeatureGate}. Pass this type to
 * `useFeatureGate` / `FeatureGateController.gatePasses` so unregistered
 * string literals fail type-check at the call site.
 */
export type FeatureGateName = (typeof FeatureGate)[keyof typeof FeatureGate];

/**
 * Dev-only resolution table for every registered feature gate. Consumed
 * by `FeatureGateController` (`@orn/core`) — both `gatePasses` and
 * `getEnabledGateNamesForViewer` — only when the server's `IS_DEV` flag
 * is `true`. In dev this map is **the sole source of truth**: the DB is
 * not consulted, so a gate doesn't need (and can't get) a `feature_gates`
 * row to evaluate true locally. In every non-dev environment (`test`,
 * `staging`, `prod`) the map is ignored entirely and gate evaluation
 * runs against `feature_gates` as before.
 *
 * **Why server-side.** Dev defaults live on the backend so there is one
 * place to manage them and the client can stay dumb — `useFeatureGate`
 * just reads what `enabledFeatureGates` returns. That keeps Storybook
 * predictable too: stories control gate state via their MSW handlers,
 * not via a parallel client-side override path.
 *
 * **Why non-`Partial`.** Every registered gate must declare its dev
 * default explicitly. Forgetting to add a new gate here is a compile
 * error rather than a silent fall-through, which used to be a common
 * tripping point ("oh man I forgot to add my gate here again"). To flip
 * a gate during testing, edit its entry below — the map is the dial.
 */
export const FEATURE_GATE_DEV_DEFAULTS: Record<FeatureGateName, boolean> = {
  [FeatureGate.Obligations]: true,
  [FeatureGate.DebugTools]: true,
  [FeatureGate.DebugTransactionDetails]: true,
  // On in dev defaults so manual chat sessions can exercise `/issue` end to
  // end. Production rollout is DB-side via the `feature_gates` table.
  [FeatureGate.AssistantCommands]: true,
  // On in dev so the enrollment flow can be exercised end to end locally,
  // where the SMS goes to a Twilio test number rather than a person. Every
  // deployed environment resolves this from `feature_gates` instead, and it
  // starts off there — this map is the local dial, not the rollout.
  [FeatureGate.LovedOneInvitations]: true,
  // Kill switch defaults OFF in dev so a local sweep runs the pass. Flip to
  // `true` to hold every recipient's rows in place.
  [FeatureGate.KillWorkItemOutreachPass]: false,
  // On in dev so local chat sessions surface tool-call details in the
  // console — the inline tool UI is gone, so this is the dev affordance for
  // inspecting what the assistant ran. Production rollout is DB-side.
  [FeatureGate.DebugAssistant]: true,
  [FeatureGate.GraphqlSubscriptions]: true,
  // On in dev so the weekly-digest default is seeded and its schedule can be
  // exercised end to end locally. Production rollout is DB-side (employee-only
  // `is_admin` at launch).
  [FeatureGate.AssistantWeeklyDigest]: true,
  // On in dev so local work on the bell and center surfaces renders without
  // flipping the map. Production rollout is DB-side.
  [FeatureGate.AlertsCenter]: true,
  // On in dev so the subscribe path runs locally against Stripe test mode.
  // Production rollout is DB-side and starts off.
  [FeatureGate.BillingCheckout]: true,
  // Off in dev so a contributor with no local subscription reaches the
  // assistant and Plaid as usual. Flip it here to exercise the wall.
  [FeatureGate.MembershipRequired]: false,
  // On in dev so a local link exercises the consent field. Production rollout
  // is DB-side and starts off.
  [FeatureGate.InvestmentAccounts]: true,
  // Off in dev: sandbox grants Auth whatever the token asks, so a local link
  // cannot tell the request apart from its absence, and leaving it off keeps
  // the local token shaped like the one deployed environments send.
  [FeatureGate.PlaidAuthConsent]: false,
  // On in dev so a sandbox link can replace a held connection end to end.
  // Production rollout is DB-side and starts off.
  [FeatureGate.ReplaceBankConnection]: true,
  // On in dev so a local evaluation raises a member's due NUX card. Production
  // rollout is DB-side and starts off.
  [FeatureGate.NuxNudges]: true,
};
